import React, { useEffect, useState, useRef } from 'react';
import '@sendbird/uikit-react/dist/index.css';
import SendbirdChat from '@sendbird/chat';
import { GroupChannelModule } from '@sendbird/chat/groupChannel';
import SendbirdProvider from '@sendbird/uikit-react/SendbirdProvider';
import CustomGroupChannel from './CustomGroupChannel';
import { checkChannelActivation } from '../services/SendbirdUserService';
import {locale, localeObject, useStringSet} from "../i18n";
import { useTranslation } from "react-i18next";

export default function SnippetComponent() {
  const { t } = useTranslation();
  const stringSet = useStringSet();
  const sbInstanceRef = useRef(null);
  const appId = process.env.REACT_APP_SENDBIRD_APP_ID;
  const queryString = globalThis.location.search;
  const queryParams = new URLSearchParams(queryString);
  const userIdQuery = queryParams.get('userId');
  const token = queryParams.get('token');
  const channelUrlQuery = queryParams.get('channelUrl');
  
  const [channelUrl, setChannelUrl] = useState(null);
  const [chatActivated, setChatActivated] = useState({ status: 1, message: '' });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Validate required parameters
    if (!appId || !userIdQuery || !channelUrlQuery) {
      setError(t('error_missing_credentials'));
      setIsLoading(false);
      return;
    }
    let isMounted = true;

    // Initialize or get existing Sendbird instance
    const initializeSendbird = () => {
      if (!sbInstanceRef.current) {
        const params = {
          appId: appId,
          modules: [new GroupChannelModule()],
        };
        sbInstanceRef.current = SendbirdChat.init(params);
      }
      return sbInstanceRef.current;
    };

    // Check channel activation status (non-critical, uses default on error)
    const updateChannelActivation = async (channelUrl, isMounted) => {
      try {
        const status = await checkChannelActivation(channelUrl, locale);
        if (isMounted) {
          setChatActivated(status);
        }
      } catch (activationError) {
        console.error('Error checking channel activation:', activationError);
        // Continue with default chatActivated value
      }
    };

    async function createConnection() {
      try {
        setIsLoading(true);
        setError(null);

        const sb = initializeSendbird();
        await sb.connect(userIdQuery, token);
        const channel = await sb.groupChannel.getChannel(channelUrlQuery);
        
        if (channel && isMounted) {
          setChannelUrl(channelUrlQuery);
          await updateChannelActivation(channel.url, isMounted);
          
          if (isMounted) {
            setIsLoading(false);
          }
        }
      } catch (error) {
        console.error('Sendbird connection error:', error);
        if (isMounted) {
          setError(error.message || t('error_loading_chat'));
          setIsLoading(false);
        }
      }
    }

    createConnection();

    // Cleanup function
    return () => {
      isMounted = false;
      if (sbInstanceRef.current) {
        sbInstanceRef.current.disconnect().catch((err) => {
          console.error('Error disconnecting Sendbird:', err);
        });
        sbInstanceRef.current = null;
      }
    };
  }, [appId, userIdQuery, channelUrlQuery, token]);

  // Loading state
  if (isLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>{t('loading_chat')}</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#D11A3B' }}>
        <div>{error}</div>
      </div>
    );
  }

  // Success state - channel loaded
  /* istanbul ignore next - defensive check, unreachable in practice */
  if (!channelUrl) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>{t('loading_chat')}</div>
      </div>
    );
  }

  return (
    <SendbirdProvider
      appId={appId}
      userId={userIdQuery}
      accessToken={token}
      stringSet={stringSet}
      dateLocale={localeObject}
    >
      <CustomGroupChannel
        channelUrl={channelUrl}
        userNickname=""
        chatStatus={chatActivated}
        showHeader={false}
      />
    </SendbirdProvider>
  );
}
